import { useEffect, useState } from "react";
import { Headphones, X, ArrowRight } from "lucide-react";
import { getAffiliateLink } from "@/lib/affiliate-registry";
import { trackAdImpression, trackAdClick } from "@/lib/ad-tracking";

const STORAGE_KEY = "mofibo-topstrip-dismissed";
const PLACEMENT = "top-strip";

export function TopAffiliateStrip() {
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(STORAGE_KEY) === "1");
    } catch {
      setDismissed(false);
    }
  }, []);

  const link = getAffiliateLink("mofibo");

  useEffect(() => {
    if (!dismissed && link) trackAdImpression(link.id, PLACEMENT);
  }, [dismissed, link]);

  if (dismissed) return null;
  if (!link) return null;

  const dismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <aside
      aria-label="Annonce: Mofibo"
      className="relative border-b border-border bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10"
    >
      <a
        href={link.url}
        target="_blank"
        rel="sponsored nofollow noopener"
        data-affiliate-id={link.id}
        data-affiliate-network={link.network}
        data-affiliate-placement={PLACEMENT}
        onClick={() => trackAdClick(link.id, PLACEMENT)}
        className="block w-full"
      >
        <div className="container mx-auto pl-3 pr-9 sm:pr-12 py-2.5 sm:py-2">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex items-center gap-2 sm:gap-3 sm:flex-1 sm:min-w-0">
              <span className="inline-flex items-center text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full shrink-0">
                Annonce
              </span>
              <Headphones className="w-4 h-4 text-primary shrink-0 hidden sm:inline" aria-hidden="true" />
              <p className="text-xs sm:text-sm text-foreground leading-snug sm:truncate sm:flex-1 sm:min-w-0">
                <span className="font-semibold">Husk lydbøgerne til ferien</span>
                <span className="hidden sm:inline text-muted-foreground sm:ml-1">— Perfekt til køreturen.</span>
              </p>
            </div>
            <div className="flex items-center justify-between gap-2 sm:justify-start sm:shrink-0">
              <span className="sm:hidden text-xs text-muted-foreground leading-snug">Perfekt til køreturen.</span>
              <span className="hidden sm:inline text-sm font-medium text-primary underline underline-offset-2 hover:text-primary/80 shrink-0">
                Prøv Mofibo gratis
              </span>
              <span className="sm:hidden inline-flex items-center gap-1 text-xs font-semibold text-primary-foreground bg-primary px-3 py-1.5 rounded-full shrink-0">
                Prøv gratis
                <ArrowRight className="w-3 h-3" aria-hidden="true" />
              </span>
            </div>
          </div>
        </div>
      </a>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Luk annonce"
        className="absolute right-1.5 top-1.5 translate-y-0 sm:top-1/2 sm:-translate-y-1/2 inline-flex items-center justify-center w-7 h-7 rounded-md hover:bg-background/60 text-muted-foreground hover:text-foreground"
      >
        <X className="w-4 h-4" />
      </button>
    </aside>
  );
}
